<script>
    import { onMount } from "svelte";

    import { allGroups, isMobile, user } from "../../stores";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        MultiSearchSelect,
        TableToggle,
        NotificationModal,
        Button,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        replaceURL,
        getText,
        showError,
        decodeSearchParams,
        encodeSearchParams,
        getBoolean,
        goBack,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import {
        AustralianStates,
        defaultState,
        DirectUserRoles,
    } from "../../Utils/utilEnums.svelte";

    import { cacheAllGroups } from "../../Utils/utilCache.svelte";
    import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserManager = $user.role == DirectUserRoles.Manager;

    let isGettingBupaPractices = false,
        returnBupaPracticesArr = [],
        filters = {},
        page = 0,
        pageSize = 25,
        count;

    let searchGroupsArr = [],
        groupOptions = [];

    let selectedBupaPracticeID = {},
        isModifyBupaPracticeModalOpen = false,
        isDeleteBupaPracticeModalOpen = false,
        isModifyingBupaPractice = false;

    $: columns = [
        {
            key: "id",
            title: "ID",
            headerClass: "text-left px-3 w-1/12 rounded-tl-xl",
            class: "text-left px-3 w-1/12 cursor-default",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "PracticeId",
            title: "Practice ID",
            headerClass: `text-left px-3 ${isUserAdmin ? "w-7/12" : "w-5/12"} cursor-default`,
            class: "text-left px-3 w-1/2 cursor-default",
            renderValue: (v) => getText(v.practiceId),
        },
        {
            key: "groupName",
            title: "Group Name",
            headerClass: "text-left px-3 w-1/6 cursor-default",
            class: "text-left px-3 w-1/6 cursor-default",
            renderValue: (v) => (v.groupName ? getText(v.groupName) : "-"),
            isHidden: !isUserAdmin,
        },
        {
            key: "isDefault",
            title: "is Default",
            headerClass: "text-left px-3 w-1/6 cursor-default",
            class: "text-left px-3 w-1/6 cursor-default",
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "isDefault",
                    clickFunction: async (rowData) =>
                        await toggleBupaPracticeIsDefault(rowData),
                },
            },
            csvContent: (v) => getBoolean(v.isDefault),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 w-1/6 cursor-default rounded-tr-xl",
            class: "text-left px-3 w-1/6 cursor-default",
            renderValue: (v) => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        if (!isUserAdmin && !isUserManager) {
            goBack();
            return;
        }

        if (params.query) updateSearchParamsByQuery(params.query);

        await loadBupaPractices(page * pageSize, pageSize, _getSearchParams());

        if (isUserAdmin) {
            await cacheAllGroups();

            groupOptions = $allGroups.map(({ id, name }) => ({
                id,
                type: name,
            }));
        }
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();

                if (arr[i].includes("groupId=") && isUserAdmin) {
                    filters["filter[groupId]"] = arr[i]
                        .split("=")[1]
                        .split(",");
                    searchGroupsArr = filters["filter[groupId]"].map(Number);
                }
            }

            updateURL();
        }
    };

    const loadBupaPractices = async (start, limit, params) => {
        isGettingBupaPractices = true;

        await apiMethods.getMethod
            .getBupaPractices(start, limit, params)
            .then((res) => _handleBupaPractices(res))
            .catch((err) => showError(err));

        isGettingBupaPractices = false;
    };

    const reloadTable = async () =>
        await apiMethods.getMethod
            .getBupaPractices(page * pageSize, pageSize, _getSearchParams())
            .then((res) => _handleBupaPractices(res))
            .catch((err) => showError(err));

    const toggleBupaPracticeIsDefault = async (data) => {
        data.isUpdating = true;

        await apiMethods.putMethod
            .updateBupaPracticeId({
                ...data,
                isDefault: !data.isDefault,
            })
            .then(async (res) => {
                if (!res || res.error) showError(res.error);
                else await reloadTable();
            });

        data.isUpdating = false;
    };

    const handleModifyBupaPracticeID = async () => {
        const isUpdate = selectedBupaPracticeID.hasOwnProperty("id");

        isModifyingBupaPractice = true;

        if (isUpdate) await updateBupaPracticeId();
        else await createBupaPracticeId();

        isModifyingBupaPractice = false;

        _closeBupaPracticeActionModal();
    };

    const createBupaPracticeId = async () => {
        await apiMethods.postMethod
            .createBupaPracticeId(selectedBupaPracticeID)
            .then(async (res) => {
                if (!res || res.error) showError(res.error);
                else await reloadTable();
            });
    };

    const updateBupaPracticeId = async () =>
        await apiMethods.putMethod
            .updateBupaPracticeId(selectedBupaPracticeID)
            .then(async (res) => {
                if (!res || res.error) showError(res.error);
                else await reloadTable();
            });

    const handleDeleteBupaPracticeID = async () => {
        isModifyingBupaPractice = true;

        await apiMethods.deleteMethod
            .deleteBupaPracticeId(selectedBupaPracticeID)
            .then(async (res) => {
                if (!res || res.error) showError(res.error);
                else await reloadTable();
            });

        isModifyingBupaPractice = false;

        _closeBupaPracticeActionModal();
    };

    const handleFilterSelect = () => {
        if (searchGroupsArr.length && isUserAdmin)
            filters["filter[groupId]"] = searchGroupsArr.join(",");
        else delete filters["filter[groupId]"];

        page = 0;

        updateURL();

        loadBupaPractices(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            if (className.includes("editIcon"))
                _openModifyBupaPracticeModal(rowData);
            else if (className.includes("deleteIcon"))
                _openDeleteBupaPracticeModal(rowData);
        }
    };

    const handleClickCol = ({ target: { className } }, rowData, key) => {};

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadBupaPractices(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadBupaPractices(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/BupaPracticeIds/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    // HELPER FUNCTIONS
    const _handleBupaPractices = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnBupaPracticesArr = [
                ...res.results.sort((a, b) => a.id - b.id),
            ];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        if (filters["filter[groupId]"] && isUserAdmin)
            params["filter[groupId]"] = filters["filter[groupId]"];

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[groupId]"])
                str += `&groupId=${params["filter[groupId]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _renderActionIcons = (hideDelete = true) =>
        `
            <div class="flex flex-1 items-center justify-start gap-x-2">
                <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Bupa Practice ID">
                    <i class="editIcon ${editIcon}"></i>
                </div>
                <div class="deleteIcon ${hideDelete ? "hidden" : ""} tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Bupa Practice ID">
                    <i class="deleteIcon ${deleteIcon}"></i>
                </div>    
            </div>
        `;

    const _openModifyBupaPracticeModal = (data = {}) => {
        selectedBupaPracticeID = { ...data };

        if (!selectedBupaPracticeID.groupId)
            selectedBupaPracticeID.groupId = $user.groupId ?? 0;

        if (!selectedBupaPracticeID.state)
            selectedBupaPracticeID.state = defaultState;

        if (!selectedBupaPracticeID.hasOwnProperty("isDefault"))
            selectedBupaPracticeID.isDefault = false;

        isModifyBupaPracticeModalOpen = true;
    };

    const _openDeleteBupaPracticeModal = (data) => {
        selectedBupaPracticeID = { ...data };
        isDeleteBupaPracticeModalOpen = true;
    };

    const _closeBupaPracticeActionModal = () => {
        isModifyBupaPracticeModalOpen = false;
        isDeleteBupaPracticeModalOpen = false;
        selectedBupaPracticeID = {};
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex items-center justify-between flex-0">
        <div class="flex gap-4 flex-wrap">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isLong
            />

            {#if isUserAdmin}
                <MultiSearchSelect
                    title="Groups"
                    bind:searchResult={searchGroupsArr}
                    optionsArr={groupOptions}
                    inputId="bupaPracticesGroupsSearchInput"
                    onChangeFunc={handleFilterSelect}
                />
            {/if}
        </div>

        <Button
            clickFunc={() => _openModifyBupaPracticeModal()}
            manualClass="w-[240px]"
        >
            Add Bupa Practice ID
        </Button>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingBupaPractices}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnBupaPracticesArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnBupaPracticesArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnBupaPracticesArr,
            fileName: "Bupa Practice IDs",
            allDataApi: async () =>
                await apiMethods.getMethod.getBupaPractices(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<NotificationModal
    isModalOpen={isModifyBupaPracticeModalOpen}
    title={`${selectedBupaPracticeID?.id ? "Edit" : "Create A New"} Bupa Practice ID`}
    cancelText="Cancel"
    closeModal={_closeBupaPracticeActionModal}
    closeModalFunction={_closeBupaPracticeActionModal}
    showConfirmBtn={false}
    btnOptions={[
        {
            text: `${isModifyingBupaPractice ? `${selectedBupaPracticeID?.id ? "Updating" : "Creating"}` : `${selectedBupaPracticeID?.id ? "Update" : "Create"}`}`,
            loading: !selectedBupaPracticeID.practiceId,
            function: async () => await handleModifyBupaPracticeID(),
        },
    ]}
>
    <div class="flex flex-col gap-y-2 p-4 w-full">
        <div class="flex gap-x-2 w-full">
            <p class="title">Practice ID:</p>
            <div class="flex-1">
                <input
                    type="text"
                    class="input input-sm input-bordered w-full"
                    bind:value={selectedBupaPracticeID.practiceId}
                />
            </div>
        </div>

        {#if isUserAdmin}
            <div class="flex gap-x-2 w-full">
                <p class="title">Group:</p>
                <select
                    class="select select-sm select-bordered flex-1"
                    bind:value={selectedBupaPracticeID.groupId}
                    disabled={selectedBupaPracticeID.id}
                >
                    <option value="0" disabled>Select group</option>
                    {#each groupOptions as { id, type }}
                        <option value={id}>{type}</option>
                    {/each}
                </select>
            </div>
        {/if}

        <div class="flex gap-x-2 w-full">
            <p class="title">State:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={selectedBupaPracticeID.state}
            >
                {#each AustralianStates as { key, name }}
                    <option value={key}>{name}</option>
                {/each}
            </select>
        </div>

        <div class="flex gap-x-2">
            <p class="title">is Default:</p>
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={selectedBupaPracticeID.isDefault}
            />
        </div>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isDeleteBupaPracticeModalOpen}
    title="Delete Bupa Practice ID"
    isReverseBtns
    submitText="Not now"
    confirmAction={_closeBupaPracticeActionModal}
    cancelText={`${isModifyingBupaPractice ? "Deleting" : "Delete"}`}
    closeModal={handleDeleteBupaPracticeID}
    closeModalFunction={_closeBupaPracticeActionModal}
>
    <p class="leading-6 p-4">Delete the selected Bupa Practice ID?</p>
</NotificationModal>

<style>
    .title {
        width: 100px;
    }
</style>
